/**
 * The software this site is about.
 *
 * @remarks
 * A site whose subject is a product needs an entity for the product, not only for the organisation
 * that publishes it. Without one the graph describes a publisher and a set of pages and stops
 * there, which is why the Rich Results Test reports `Organization` and `Breadcrumbs` and nothing
 * else — there is no software in the graph for it to find.
 *
 * Two entities come from this file, declared in `entities.ts`:
 *
 * - **`SoftwareApplication`** (or `WebApplication` / `MobileApplication`, see
 *   {@link SoftwareConfig.type}) — the product. This is the one Google reads for the Software App
 *   rich result.
 * - **`SoftwareSourceCode`** — its repository. Genuinely a second thing: the product is what a
 *   visitor uses, the source is what a contributor clones.
 *
 * **Eligibility is not the same as validity.** Google requires `name`, an `offers` with a `price`,
 * and *either* an `aggregateRating` or a `review`. The first two are set here. The third cannot be:
 * a rating has to come from people who actually rated the software, so it is derived from
 * `testimonials.ts` and is absent until that file has entries. See the remarks there.
 *
 * Set {@link SoftwareConfig.enabled} to `false` on a site that is not about a piece of software —
 * both entities disappear and nothing else changes.
 *
 * **Relative imports only** — this file is reachable from `vite.config.ts`.
 * See AGENTS.md § The Thing That Breaks Silently.
 *
 * @module
 * @author Bayu Dwiyan Satria
 * @since 1.0.0
 */

/**
 * Shape of {@link softwareConfig}.
 */
export interface SoftwareConfig {
  /**
   * Whether the site is about a piece of software at all.
   *
   * @remarks
   * `false` omits the `SoftwareApplication` and `SoftwareSourceCode` entities entirely. An entity
   * describing a product that does not exist is worse than no entity.
   */
  readonly enabled: boolean
  /**
   * Which Schema.org application type to declare.
   *
   * @remarks
   * `'WebApplication'` for something used in a browser, `'MobileApplication'` for an app store
   * listing, `'SoftwareApplication'` for everything else. All three are eligible for the same rich
   * result; pick the one that is literally true.
   */
  readonly type: 'SoftwareApplication' | 'WebApplication' | 'MobileApplication'
  /** Product name. Required by Google, and the name the rich result displays. */
  readonly name: string
  /** One-sentence summary of what the software does. */
  readonly description: string
  /** Schema.org application category, e.g. `DeveloperApplication`, `BusinessApplication`. */
  readonly applicationCategory: string
  /** What it runs on. `'Any'` for something with no platform requirement. */
  readonly operatingSystem: string
  /**
   * Released version, emitted as `softwareVersion`.
   *
   * @remarks
   * Kept as a literal rather than imported from `package.json`: this layer is bundled into the
   * client, and importing the manifest would inline the entire dependency list into the app chunk.
   * `test/config/entities.spec.ts` asserts the two agree, which is the part a comment cannot do.
   */
  readonly version: string
  /** Licence URL — the canonical one, e.g. `https://opensource.org/licenses/MIT`. */
  readonly license: string
  /** Public source repository, emitted as `codeRepository`. */
  readonly repository: string
  /** Languages the source is written in. */
  readonly programmingLanguage: readonly string[]
  /** Runtime the source targets, e.g. `Node.js`. */
  readonly runtimePlatform: string
  /**
   * Headline capabilities, emitted as `featureList`.
   *
   * @remarks
   * These are the feature cards `HomeView.vue` renders, in the site language. Structured data
   * describing capabilities the page does not mention is the same mistake as an FAQ whose questions
   * are not on the page; a test keeps the two lists identical.
   */
  readonly featureList: readonly string[]
  /**
   * A real screenshot of the software, absolute URL or site-relative path.
   *
   * @remarks
   * Empty to omit. A logo or a share image is not a screenshot, and passing one off as such is a
   * claim about the interface that the file does not support.
   */
  readonly screenshot: string
  /**
   * What it costs.
   *
   * @remarks
   * Required for the rich result — `price: 0` is how free software states its price, and
   * `priceCurrency` is mandatory whenever a price is present.
   */
  readonly offers: {
    readonly price: number
    readonly priceCurrency: string
  }
}

/**
 * The live software configuration.
 *
 * @example
 * ```ts
 * import { softwareConfig } from '@/config/software'
 *
 * const version = softwareConfig.version
 * ```
 */
export const softwareConfig: SoftwareConfig = {
  enabled: true,
  type: 'SoftwareApplication',
  name: 'Vue Boilerplate',
  description:
    'A production-ready Vue 3 starter that prerenders every route to static HTML with its own metadata and Schema.org structured data already in the markup.',
  applicationCategory: 'DeveloperApplication',
  operatingSystem: 'Any',
  version: '1.0.0',
  license: 'https://opensource.org/licenses/MIT',
  repository: 'https://github.com/bayudwiyansatria/vuejs-boilerplate',
  programmingLanguage: ['TypeScript', 'Vue'],
  runtimePlatform: 'Node.js',
  /*
   * The English titles of the four cards on the home route, in the order they are rendered. Change
   * one and the other has to change with it — the test that pins them together says which.
   */
  featureList: ['Prerendered routes', 'Structured data graph', 'Generated sitemap and robots', 'Enforced layering'],
  screenshot: '',
  offers: {
    price: 0,
    priceCurrency: 'USD'
  }
}
